/**
 * Utility to parse Local Listing import Excel files (.xlsx/.xls)
 * Parses only the data from Excel - everything else is handled by existing functions.
 */

import Logger, { LOG_LEVELS } from './logger';

const log = new Logger('LOCAL_LISTING_EXCEL_PARSER', LOG_LEVELS.DEBUG);

/**
 * Expected column headers in the template file
 * Maps to: VATNumber, Amount Excl. VAT, Amount VAT, Total Amount
 */
const EXPECTED_HEADERS = {
  VAT_NUMBER: 'VATNumber',
  AMOUNT_EXCL_VAT: 'Amount Excl. VAT',
  AMOUNT_VAT: 'Amount VAT',
  TOTAL_AMOUNT: 'Total Amount'
};

/**
 * Default listing type for Sales (Out = 0)
 * Since In/Out column is not in the template, we default to Sales/Out
 */
const DEFAULT_LISTING_TYPE = 0; // Out/Sales

/**
 * Validates that the file has the expected headers
 */
const validateHeaders = (headers) => {
  if (!Array.isArray(headers) || headers.length < 4) {
    return false;
  }
  
  const normalizedHeaders = headers.map(h => String(h).trim());
  return normalizedHeaders.includes(EXPECTED_HEADERS.VAT_NUMBER) &&
         normalizedHeaders.includes(EXPECTED_HEADERS.TOTAL_AMOUNT) &&
         normalizedHeaders.includes(EXPECTED_HEADERS.AMOUNT_EXCL_VAT) &&
         normalizedHeaders.includes(EXPECTED_HEADERS.AMOUNT_VAT);
};

/**
 * Validates a single row of data from the Excel file.
 * Checks required fields (VAT Number, Total Amount) and validates numeric fields.
 * 
 * @param {object} row - Row object with vatNumber, totalAmount, amountExclVAT, amountVAT
 * @returns {object} Validation result with isValid (boolean) and errors (array of strings)
 */
const validateRow = (row) => {
  const errors = [];
  
  // Validate VAT Number (required)
  if (!row.vatNumber || String(row.vatNumber).trim() === '') {
    errors.push('VATNumber is required');
  }
  
  // Validate Total Amount (required, must be numeric)
  if (row.totalAmount === undefined || row.totalAmount === null || row.totalAmount === '') {
    errors.push('Total Amount is required');
  } else if (isNaN(parseFloat(row.totalAmount))) {
    errors.push('Total Amount must be a number');
  }
  
  // Validate Amount Excl VAT (optional, but must be numeric if provided)
  if (row.amountExclVAT !== undefined && row.amountExclVAT !== null && row.amountExclVAT !== '') {
    if (isNaN(parseFloat(row.amountExclVAT))) {
      errors.push('Amount Excl. VAT must be a number');
    }
  }
  
  // Validate Amount VAT (optional, but must be numeric if provided)
  if (row.amountVAT !== undefined && row.amountVAT !== null && row.amountVAT !== '') {
    if (isNaN(parseFloat(row.amountVAT))) {
      errors.push('Amount VAT must be a number');
    }
  }
  
  return { isValid: errors.length === 0, errors };
};

/**
 * Parses raw sheet data into Local Listing line items.
 * Returns minimal structure matching existing grid data format.
 * Validates each row and collects errors for invalid rows.
 * 
 * @param {Array} data - Array of row objects from Excel sheet
 * @returns {object} Result object with:
 *   - lines: Array of valid parsed line items
 *   - errors: Array of error messages for invalid rows
 */
const parseSheetData = (data) => {
  const lines = [];
  const errors = [];
  
  if (!Array.isArray(data) || data.length === 0) {
    errors.push('No data found in file');
    return { lines, errors };
  }
  
  data.forEach((row, index) => {
    const rowNumber = index + 2;
    const validation = validateRow(row);
    
    if (!validation.isValid) {
      errors.push(`Row ${rowNumber}: ${validation.errors.join(', ')}`);
      return;
    }
    
    const vatNumber = String(row.vatNumber).trim();
    const totalAmount = parseFloat(row.totalAmount) || 0;
    const amountExclVAT = parseFloat(row.amountExclVAT) || 0;
    const amountVAT = parseFloat(row.amountVAT) || 0;
    
    // Minimal structure matching existing grid format
    // Default to Sales/Out (0) since In/Out column is not in the template
    lines.push({
      vatNumber: vatNumber,
      totalAmount: totalAmount,
      amountExclVAT: amountExclVAT,
      amountVAT: amountVAT,
      listingLineType: DEFAULT_LISTING_TYPE,
      New: true
    });
  });
  
  return { lines, errors };
};

/**
 * Parses an Excel file (.xlsx/.xls) and extracts Local Listing data.
 * Uses dynamic import for xlsx library to support code splitting.
 * 
 * @async
 * @param {File} file - The Excel file to parse
 * @returns {Promise<object>} Result object with:
 *   - success: boolean indicating if parsing was successful
 *   - lines: Array of parsed Local Listing line items
 *   - errors: Array of error messages if any
 */
export const parseLocalListingExcel = async (file) => {
  log.debug('parseLocalListingExcel: Parsing file', file.name);
  
  try {
    const XLSX = await import('xlsx');
    
    return new Promise((resolve) => {
      const reader = new FileReader();
      
      reader.onload = (event) => {
        try {
          const data = new Uint8Array(event.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          
          const firstSheetName = workbook.SheetNames[0];
          if (!firstSheetName) {
            resolve({ success: false, lines: [], errors: ['No sheets found in the file'] });
            return;
          }
          
          const worksheet = workbook.Sheets[firstSheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          
          if (jsonData.length < 2) {
            resolve({ success: false, lines: [], errors: ['File must contain at least a header row and one data row'] });
            return;
          }
          
          const headers = jsonData[0];
          if (!validateHeaders(headers)) {
            resolve({ success: false, lines: [], errors: ['Invalid file format. Expected columns: VATNumber, Amount Excl. VAT, Amount VAT, Total Amount'] });
            return;
          }
          
          // Find column indices dynamically based on header positions
          const normalizedHeaders = headers.map(h => String(h).trim());
          const vatNumberIdx = normalizedHeaders.indexOf(EXPECTED_HEADERS.VAT_NUMBER);
          const amountExclVATIdx = normalizedHeaders.indexOf(EXPECTED_HEADERS.AMOUNT_EXCL_VAT);
          const amountVATIdx = normalizedHeaders.indexOf(EXPECTED_HEADERS.AMOUNT_VAT);
          const totalAmountIdx = normalizedHeaders.indexOf(EXPECTED_HEADERS.TOTAL_AMOUNT);
          
          const dataRows = jsonData.slice(1).filter(row => row.length > 0);
          // Map columns dynamically using found indices
          const objectData = dataRows.map(row => ({
            vatNumber: row[vatNumberIdx],
            amountExclVAT: row[amountExclVATIdx],
            amountVAT: row[amountVATIdx],
            totalAmount: row[totalAmountIdx]
          }));
          
          const { lines, errors } = parseSheetData(objectData);
          resolve({ success: errors.length === 0, lines, errors });
        } catch (parseError) {
          log.debug('parseLocalListingExcel: Parse error', parseError);
          resolve({ success: false, lines: [], errors: [`Failed to parse file: ${parseError.message}`] });
        }
      };
      
      reader.onerror = () => {
        resolve({ success: false, lines: [], errors: ['Failed to read file'] });
      };
      
      reader.readAsArrayBuffer(file);
    });
  } catch (importError) {
    log.debug('parseLocalListingExcel: Import error', importError);
    return { success: false, lines: [], errors: ['Excel parsing library not available'] };
  }
};

export default {
  parseLocalListingExcel,
  validateHeaders,
  validateRow,
  EXPECTED_HEADERS
};
